import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
    ArrowRight,
    Blocks,
    HeartHandshake,
    ShieldCheck,
    Apple,
    Sun,
    Star,
    Sparkles,
    MessageCircle,
} from "lucide-react";
import { MaskedLines, FadeUp, Stagger, Item } from "../components/Reveal";
import { BeeMark } from "../components/Logo";
import {
    FloatingBee,
    FlightPath,
    HexPattern,
    RotatingBadge,
    Wave,
} from "../components/Bee";
import Marquee from "../components/Marquee";
import ContactBanner from "../components/ContactBanner";
import { SITE, IMAGES, PROGRAMS, TESTIMONIALS } from "../data/site";

const BENTO = [
    {
        id: "play",
        span: "sm:col-span-2 lg:col-span-7",
        bg: "bg-honey",
        title: "Learning through play",
        copy: "Every song, splash and tower of blocks is secretly building early literacy, math and problem-solving. Around here, play is the curriculum — and joy is the proof.",
        icon: Blocks,
        big: true,
    },
    {
        id: "teachers",
        span: "lg:col-span-5",
        bg: "bg-sage",
        title: "Nurturing educators",
        copy: "Warm, qualified teachers who learn each child's spark and build on it.",
        icon: HeartHandshake,
    },
    {
        id: "safe",
        span: "lg:col-span-4",
        bg: "bg-skysoft",
        title: "Safe & secure",
        copy: "Secure entry, first-aid certified staff and squeaky-clean rooms all day.",
        icon: ShieldCheck,
    },
    {
        id: "fresh",
        span: "lg:col-span-4",
        bg: "bg-peach",
        title: "Fresh & tasty",
        copy: "Balanced snacks and lunch, with allergies happily accommodated.",
        icon: Apple,
    },
    {
        id: "outdoor",
        span: "sm:col-span-2 lg:col-span-4",
        bg: "bg-lav",
        title: "Outdoor discovery",
        copy: "Garden beds, gross-motor play and fresh air woven into every single day.",
        icon: Sun,
    },
];

export default function Home() {
    useEffect(() => {
        document.title = `${SITE.name} | Playful Preschool & Childcare`;
    }, []);

    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);
    const bgY = useTransform(scrollYProgress, [0, 1], [0, -60]);

    return (
        <>
            {/* ---------- HERO ---------- */}
            <section
                ref={heroRef}
                data-testid="hero-section"
                className="relative overflow-hidden pb-14 pt-28 sm:pt-36 lg:pb-20"
            >
                <HexPattern className="pointer-events-none absolute -right-24 top-16 hidden h-[540px] w-[760px] rotate-6 lg:block" />
                <motion.div
                    style={{ y: bgY }}
                    className="pointer-events-none absolute -left-32 top-24 h-80 w-80 rounded-full bg-highlight opacity-60 blur-3xl"
                />
                <div className="wrap relative grid items-center gap-14 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10">
                    <div>
                        <FadeUp delay={0.55}>
                            <span className="eyebrow" data-testid="hero-eyebrow">
                                <Sparkles className="h-3.5 w-3.5" />
                                Now enrolling — join the hive
                            </span>
                        </FadeUp>
                        <h1 className="mt-6 font-display text-[clamp(2.8rem,7vw,5.4rem)] font-semibold leading-[1.04] tracking-tight text-ink">
                            <MaskedLines
                                delay={0.15}
                                lines={[
                                    "Where little",
                                    <>
                                        learners{" "}
                                        <span className="text-honey-warm">
                                            buzz
                                        </span>
                                        <BeeInline />
                                    </>,
                                    "with joy.",
                                ]}
                            />
                        </h1>
                        <FadeUp delay={0.6}>
                            <p className="mt-6 max-w-md text-lg leading-relaxed text-ink-muted">
                                A bright, buzzing preschool & childcare where
                                ages 6 weeks to 5 years learn through play,
                                music, mud kitchens and mountains of books.
                            </p>
                        </FadeUp>
                        <FadeUp delay={0.7}>
                            <div className="mt-8 flex flex-wrap items-center gap-4">
                                <a
                                    href={SITE.whatsappHref}
                                    data-testid="hero-book-tour-btn"
                                    className="btn-honey"
                                    aria-label="Book a tour on WhatsApp"
                                >
                                    <MessageCircle className="h-4 w-4" />
                                    Book a Tour
                                </a>
                                <Link
                                    to="/programs"
                                    data-testid="hero-explore-programs-btn"
                                    className="btn-ghost"
                                >
                                    Explore Programs
                                </Link>
                            </div>
                        </FadeUp>
                        <FadeUp delay={0.8}>
                            <div className="mt-9 flex items-center gap-3 text-sm text-ink-muted">
                                <span className="flex gap-0.5">
                                    {Array.from({ length: 5 }).map((_, i) => (
                                        <Star
                                            key={i}
                                            className="h-4 w-4 fill-honey text-honey"
                                        />
                                    ))}
                                </span>
                                Loved by local families
                                <span className="hidden h-1 w-1 rounded-full bg-ink/30 sm:inline-block" />
                                <span className="hidden sm:inline">
                                    Licensed early learning academy
                                </span>
                            </div>
                        </FadeUp>
                    </div>
                    <div className="relative mx-auto w-full max-w-[520px]">
                        <FlightPath className="pointer-events-none absolute -left-28 bottom-2 hidden h-56 w-80 md:block" />
                        <motion.div
                            style={{ y: imgY }}
                            className="relative"
                            data-testid="hero-photo-frame"
                        >
                            <div className="overflow-hidden rounded-b-[3rem] rounded-t-[999px] border-8 border-white shadow-[0_30px_80px_rgba(185,125,10,0.22)]">
                                <img
                                    src={IMAGES.hero}
                                    alt="Busy Bees children enjoying an outdoor learning activity"
                                    className="aspect-square w-full object-cover object-center"
                                    data-testid="hero-photo"
                                />
                            </div>
                            <RotatingBadge className="absolute -left-2 top-8 h-24 w-24 rounded-full bg-paper/70 backdrop-blur-sm sm:-left-9 sm:top-10 sm:h-28 sm:w-28" />
                            <FloatingBee className="-right-3 bottom-24 sm:-right-8" />
                            <span
                                data-testid="hero-ages-chip"
                                className="absolute bottom-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-white px-5 py-2.5 font-display text-sm font-semibold text-ink shadow-lg"
                            >
                                {SITE.ages}
                            </span>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* ---------- MARQUEE ---------- */}
            <Marquee
                testid="home-marquee"
                items={[
                    "Playful Learning",
                    "Nurturing Care",
                    "Creative Discovery",
                    "Social Growth",
                    "Safe Environment",
                    "Outdoor Adventure",
                ]}
            />

            {/* ---------- VALUES BENTO ---------- */}
            <section
                data-testid="values-bento-grid"
                className="wrap py-16 lg:py-24"
            >
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div className="max-w-xl">
                        <FadeUp>
                            <span className="eyebrow">Why Busy Bees</span>
                        </FadeUp>
                        <FadeUp delay={0.08}>
                            <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                                A hive built for happy childhoods
                            </h2>
                        </FadeUp>
                    </div>
                    <FadeUp delay={0.15}>
                        <p className="max-w-sm leading-relaxed text-ink-muted">
                            Thoughtful rooms, gentle rhythms and teachers who
                            genuinely delight in your child — every single day.
                        </p>
                    </FadeUp>
                </div>
                <Stagger
                    className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-12"
                    gap={0.07}
                >
                    {BENTO.map((tile) => (
                        <Item
                            key={tile.id}
                            className={`${tile.span} h-full`}
                            data-testid={`bento-tile-${tile.id}`}
                        >
                            <div
                                data-testid={`bento-tile-${tile.id}`}
                                className={`card-lift relative h-full overflow-hidden rounded-3xl p-6 sm:p-8 ${tile.bg}`}
                            >
                                {tile.big && (
                                    <HexPattern
                                        className="pointer-events-none absolute inset-0 h-full w-full"
                                        stroke="#FFFFFF"
                                        opacity={0.25}
                                    />
                                )}
                                <div className="relative">
                                    <span
                                        className={`flex h-12 w-12 items-center justify-center rounded-full ${
                                            tile.big
                                                ? "bg-white/50"
                                                : "bg-white/70"
                                        }`}
                                    >
                                        <tile.icon className="h-6 w-6 text-honey-warm" />
                                    </span>
                                    <h3
                                        className={`mt-5 font-display font-semibold ${
                                            tile.big
                                                ? "text-3xl sm:text-4xl"
                                                : "text-xl sm:text-2xl"
                                        }`}
                                    >
                                        {tile.title}
                                    </h3>
                                    <p
                                        className={`mt-2.5 leading-relaxed ${
                                            tile.big
                                                ? "max-w-md text-ink/75 sm:text-lg"
                                                : "text-sm text-ink/70"
                                        }`}
                                    >
                                        {tile.copy}
                                    </p>
                                </div>
                            </div>
                        </Item>
                    ))}
                </Stagger>
            </section>

            {/* ---------- PROGRAMS PREVIEW ---------- */}
            <section
                data-testid="programs-preview-grid"
                className="wrap pb-16 lg:pb-24"
            >
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div className="max-w-xl">
                        <FadeUp>
                            <span className="eyebrow">Our Programs</span>
                        </FadeUp>
                        <FadeUp delay={0.08}>
                            <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                                Programs for every stage of little
                            </h2>
                        </FadeUp>
                    </div>
                    <FadeUp delay={0.15}>
                        <Link
                            to="/programs"
                            data-testid="programs-preview-see-all-btn"
                            className="group inline-flex items-center gap-2 font-display font-semibold text-honey-warm transition-colors hover:text-honey-deep"
                        >
                            See all programs
                            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                    </FadeUp>
                </div>
                <Stagger
                    className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4"
                    gap={0.08}
                >
                    {PROGRAMS.map((p) => (
                        <Item key={p.id} className="h-full">
                            <Link
                                to="/programs"
                                data-testid={`program-card-${p.id}`}
                                className="group block h-full"
                            >
                                <div className="relative overflow-hidden rounded-b-3xl rounded-t-[10rem] border-4 border-white shadow-[0_18px_44px_rgba(185,125,10,0.16)]">
                                    <img
                                        src={p.image}
                                        alt={`${p.name} — ${p.group}`}
                                        loading="lazy"
                                        className="aspect-[4/3.6] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                                    />
                                    <span className="absolute left-1/2 top-5 -translate-x-1/2 whitespace-nowrap rounded-full bg-white/95 px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-honey-warm shadow">
                                        {p.age}
                                    </span>
                                </div>
                                <h3 className="mt-4 flex items-center gap-2 font-display text-xl font-semibold">
                                    {p.name}
                                    <ArrowRight className="h-4 w-4 -translate-x-1 text-honey-warm opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100" />
                                </h3>
                                <p className="mt-1.5 text-sm leading-relaxed text-ink-muted">
                                    {p.description.split(".")[0]}.
                                </p>
                            </Link>
                        </Item>
                    ))}
                </Stagger>
            </section>

            {/* ---------- TESTIMONIALS ---------- */}
            <section data-testid="testimonials-section" className="relative">
                <Wave fill="#FFF9E6" />
                <div className="-mt-px bg-warm pb-14 pt-8 lg:pb-16">
                    <div className="wrap">
                        <div className="mx-auto max-w-xl text-center">
                            <FadeUp>
                                <span className="eyebrow">Happy families</span>
                            </FadeUp>
                            <FadeUp delay={0.08}>
                                <h2 className="mt-4 font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">
                                    Buzz from our parents
                                </h2>
                            </FadeUp>
                        </div>
                        <Stagger
                            className="mt-10 grid gap-5 lg:grid-cols-3"
                            gap={0.1}
                        >
                            {TESTIMONIALS.map((t) => (
                                <Item key={t.name} className="h-full">
                                    <figure className="card-lift flex h-full flex-col rounded-3xl border border-ink/5 bg-white p-7">
                                        <div className="flex gap-1">
                                            {Array.from({ length: 5 }).map(
                                                (_, i) => (
                                                    <Star
                                                        key={i}
                                                        className="h-4 w-4 fill-honey text-honey"
                                                    />
                                                ),
                                            )}
                                        </div>
                                        <blockquote className="mt-4 flex-1 font-display text-lg font-medium leading-relaxed text-ink/90">
                                            “{t.quote}”
                                        </blockquote>
                                        <figcaption className="mt-5 text-sm">
                                            <span className="font-semibold text-ink">
                                                {t.name}
                                            </span>
                                            <span className="text-ink-soft">
                                                {" "}
                                                · {t.detail}
                                            </span>
                                        </figcaption>
                                    </figure>
                                </Item>
                            ))}
                        </Stagger>
                    </div>
                </div>
                <Wave fill="#FFF9E6" className="rotate-180" />
            </section>

            {/* ---------- CONTACT STRIP ---------- */}
            <ContactBanner testid="contact-strip-footer" />
        </>
    );
}

function BeeInline() {
    return (
        <motion.span
            aria-hidden="true"
            className="ml-1 inline-block h-[0.72em] w-[0.72em] align-[-0.06em]"
            animate={{ y: [0, -6, 0], rotate: [0, 8, 0] }}
            transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
        >
            <BeeMark className="h-full w-full" />
        </motion.span>
    );
}
